import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ujmsFinanceCatalog, ujmsAssess, ujmsTakePayment, ujmsAdjust, ujmsWaive, ujmsCreatePlan,
} from "../../services/ujmsApi";
import { inputCls, selectCls, Field, btnPrimary, btnGhost } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const TITLES = { assess: "Add Assessment", pay: "Take Payment", adjust: "Adjustment", waive: "Waive Amount", plan: "Payment Plan" };

export function FinanceActionDialog({ action, onClose, onDone }) {
  const [catalog, setCatalog] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (action) { setF({ category: "COURT_COSTS", method: "CASH" }); ujmsFinanceCatalog().then((r) => setCatalog(r.data)).catch(() => {}); }
  }, [action]);
  if (!action) return null;
  const { type, account } = action;

  const submit = async () => {
    setBusy(true);
    try {
      let res;
      if (type === "assess") res = await ujmsAssess(account.id, { amount: Number(f.amount), category: f.category, reason: f.reason || "" });
      if (type === "pay") res = await ujmsTakePayment(account.id, { amount: Number(f.amount), method: f.method, payer_name: f.payer_name || "", note: f.note || "" });
      if (type === "adjust") res = await ujmsAdjust(account.id, { amount: Number(f.amount), reason: f.reason || "" });
      if (type === "waive") res = await ujmsWaive(account.id, { amount: Number(f.amount), reason: f.reason || "" });
      if (type === "plan") res = await ujmsCreatePlan(account.id, { monthly_amount: Number(f.amount), start_date: f.start_date });
      const receipt = res?.data?.receipt?.receipt_number;
      toast.success(type === "pay" ? `Payment recorded — Receipt ${receipt}` : `${TITLES[type]} posted to ledger`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="finance-action-dialog">
        <DialogHeader><DialogTitle>{TITLES[type]} — {account.party_name || account.case_number || "Account"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={type === "plan" ? "Monthly amount ($)" : type === "adjust" ? "Amount ($, +/-)" : "Amount ($)"} required>
              <input type="number" step="0.01" className={inputCls} value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="fin-amount-input" />
            </Field>
            {type === "assess" && (
              <Field label="Category">
                <select className={selectCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="fin-category-select">
                  {(catalog?.categories || []).map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
            )}
            {type === "pay" && (
              <Field label="Method">
                <select className={selectCls} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="fin-method-select">
                  {(catalog?.payment_methods || []).map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
            )}
            {type === "plan" && (
              <Field label="Start date" required>
                <input type="date" className={inputCls} value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="fin-plan-start" />
              </Field>
            )}
          </div>
          {type === "pay" && (
            <Field label="Payer name" required>
              <input className={inputCls} value={f.payer_name || ""} onChange={(e) => setF({ ...f, payer_name: e.target.value })} data-testid="fin-payer-input" />
            </Field>
          )}
          {type !== "plan" && type !== "pay" && (
            <Field label="Reason" required>
              <input className={inputCls} value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="fin-reason-input" />
            </Field>
          )}
          {type === "pay" && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
              Card payments are processed through the SANDBOX provider abstraction — no card credentials are collected or stored.
            </div>
          )}
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="fin-submit-btn">Post</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
